import { englishAccentRules } from "./accentRules";
import { chainShiftRules } from "./chainShift";
import { ipaToEnglish } from "./ipaToEnglish";
import { getIpaToCxs } from "./cxs";
import { makeRules } from "../rules/makeRules";
import { Rule } from "../rules/rule";
import { RulesRunner } from "../rules/rulesRunner";
import { RuleSet } from "../rules/ruleSet";
import { americanizedSpanish } from "./americanize";
import { mexicanAccent, spanishAccent } from "./spanishToIpa";

/** sets of sample phoneme-modifying rules */
export class SampleRules {
  public runners: Record<string, RulesRunner> = {};

  public getNames(): string[] {
    return Object.getOwnPropertyNames(this.runners);
  }

  public constructor() {
    const accentNames = Object.keys(englishAccentRules);
    accentNames.forEach(name => this.add('Accent: ' + name, englishAccentRules[name]));

    this.add("Spanish: Spain", spanishAccent);
    this.add("Spanish: Mexico", mexicanAccent);
    this.add("Spanish: Americanized", americanizedSpanish);

    const shiftNames = Object.keys(chainShiftRules);
    shiftNames.forEach(name => this.add(name, chainShiftRules[name]));

    // https://en.wikipedia.org/wiki/Lenition (and generalized slightly)
    // voiceless plosive -> voiced -> fricative -> approximant -> ∅
    const sonorization = [['p', 'b', 'v', 'ʋ'], ['t', 'd', 'z', 'ɹ'], ['ʈ', 'ɖ', 'ʐ', 'ɻ'],
    ['c', 'ɟ', 'ʝ', 'j'], ['k', 'ɡ', 'ɣ', 'j'], ['q', 'ɢ', 'ʁ', 'ɰ']];
    this.add('lenition (sonorization)', makeRules(sonorization, true));
    this.add('fortition (sonorization)', makeRules(sonorization, false));
    // voiceless plosive -> affricate -> fricative -> h -> ∅
    const opening1 = [['p', 'pɸ', 'ɸ', 'h', ''], ['t̪', 'tθ', 'θ', 'h'], ['t', 'tθ', 'θ', 'h'],
    ['ʈ', 'ʈʂ', 'ʂ', 'h'], ['c', 'cç', 'ç', 'h'], ['k', 'kx', 'x', 'h'], ['q', 'qχ', 'χ', 'h']];
    const opening2 = [['p', 'pf', 'f', 'h', ''], ['t', 'ts', 's', 'h'], ['k', 'kx', 'x', 'h']];
    this.add('lenition (opening1)', makeRules(opening1, true));
    this.add('lenition (opening2)', makeRules(opening2, true));

    // keep all features the same except voiced/voiceless
    const voicePairs = [
      ['b', 'p'], ['d', 't'], ['g', 'k'], ['ɡ', 'k'], ['v', 'f'], ['z', 's'],
      ['β', 'ɸ'], ['ð', 'θ'], ['ɢ', 'q'], ['ɣ', 'x'], ['ʒ', 'ʃ'], ['ɓ', 'ƥ'],
      ['ɗ', 'ƭ'], ['ʄ', 'ƈ'], ['ɠ', 'ƙ'], ['ʛ', 'ʠ'], ['ʢ', 'ʜ'], ['ʑ', 'ɕ'],
    ];
    this.add('shift voiceless', makeRules(voicePairs, true));
    this.add('shift voiced', makeRules(voicePairs, false));

    // move English phonemes forward/backward in the mouth
    const positionChains = [
      ['tʃ', 'tʃ'], ['dʒ', 'dʒ'], // don't change the t or d in these
      ['k', 't', 'p'], ['g', 'd', 'b'],
      ['ŋ', 'n', 'm'],
      ['s', 'θ', 'f'], ['z', 'ð', 'v'],
      ['u', 'ʊ', 'ɪ', 'i'], ['uː', 'ʊː', 'ɪː', 'iː'],
      ['ʌ', 'ə', 'ɛ'], ['ʌː', 'əː', 'ɛː'],
      ['a', 'æ'], ['aː', 'æː'],
    ];
    this.add('shift forward', makeRules(positionChains, true));
    this.add('shift backward', makeRules(positionChains, false));

    this.add('CXS (Conlang X-SAMPA)', getIpaToCxs());
    this.add('pseudo-English spelling', ipaToEnglish);
  }

  private add(name: string, rules: RuleSet | Rule[]): void {
    const runner = new RulesRunner(rules);
    this.runners[name] = runner;
  }
}

